import { ReactComponent as PlusIcon } from "../../../../assets/icons/trans_plus_ico.svg";
import { ReactComponent as FilterIcon } from "../../../../assets/icons/filter-icon.svg";
import { ReactComponent as ArrowIcon } from "../../../../assets/icons/sort_arrow.svg";
import { LanguageContext } from "../../../../context/LanguageContext";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";

import {
  AmountBox,
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
} from "../../../common";
import React, { useContext, useEffect, useMemo, useState } from "react";
import UBSearchBar from "../../../../Libraries/UBSearchBar";
import withObservables from "@nozbe/with-observables";
import { Grid } from "@mui/material";
import {
  observeBillInvoicesByBusiness,
  observeSaleInvoicesByBusiness,
} from "../../../../database/helpers/invoice";
import { CircularProgress, Box } from "@mui/material";
import { INVOICESORT } from "../../../../constants";
import { ParagraphText, UBButton, AppPagination } from "../../../../Libraries";
import { useAllInvoiceReport } from "../../../../hooks/useInvoiceList";
import { formatNum } from "../../../../oscar-pos-core/constants";
import { InvoiceBookNonIdeal } from "../../../common/NewNonIdeal";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../../context/AuthContext";
import InvoiceRowItem from "./InvoiceRowItem";
import FilterModal from "../InvoiceBookComponents/FilterModal";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { dateFormatInNumberWithSpace } from "../../../../utils";
// type "in" means sale invoice
// type "out" means purchase invoice

const InvoicePageContent = {
  title: {
    in: "Sales invoice",
    out: "Purchase bill",
  },
  search_placeholder: {
    in: "Search by name or invoice#",
    out: "Search by name or PO#",
  },
  create_btn: {
    in: "Create sales invoice",
    out: "Create purchase bill",
  },
  invoice_info1: {
    in: "Paid invoice",
    out: "Paid bills",
  },
  invoice_info2: {
    in: "Unpaid invoice",
    out: "Unpaid bills",
  },
  invoice_info3: {
    in: "Total Profit",
    out: "",
    displayOn: "in",
  },
  defaultPrefix: {
    in: "INV#",
    out: "PO#",
  },
};
export const SortArrow = ({ isAscending, id, disabled }) => (
  <ArrowIcon
    id={id}
    style={{
      ...(isAscending && { transform: "rotate(-0.5turn)" }),
      ...(disabled && { opacity: 0.3, pointerEvents: "none" }),
    }}
  />
);

export const InvListTableHeads = [
  {
    name: "Date",
    isArrow: true,
    onClick: (setDirection) => {
      setDirection((c) => !c);
    },
    showDataKey: "date",
    className: "textLeft",
    bodyClassName: "newFontColor",
  },
  {
    name: "Customer",
    outName: "Suppliers",
    showDataKey: "cust_name",
    className: "textLeft",
    bodyClassName: "newFontColor",
  },
  {
    name: "Invoice Number",
    showDataKey: "inv_num",
    outName: "PO Number",
    className: "textLeft",
    bodyClassName: "newFontColor",
  },

  {
    name: "Due date",
    showDataKey: "due_date",
    className: "textLeft",
    bodyClassName: "newFontColor",
  },
  {
    name: "Status",
    isArrow: true,
    outName: "Status",
    showDataKey: "status",
    className: "textLeft",
    bodyClassName: "newFontColor",
  },

  {
    name: "Amount",
    showDataKey: "amount",
    rsLabel: true,
    className: "textLeft",
    bodyClassName: "newFontColor",
  },

  {
    name: "Balance",
    showDataKey: "balance",
    rsLabel: true,
    className: "textLeft",
    bodyClassName: "newFontColor",
  },

  {
    name: "Options",
    showActionButton: true,
    className: "textRight",
    bodyClassName: "newFontColor",
  },
];

const InvoiceList = ({ type = "in", invoiceList: invoiceListTemp }) => {
  const { business } = useAuthContext();
  const initialFilters = {
    payment: "All",
    range: [],
    sort: "Recent",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [showFilter, setShowFilter] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [pageSize, setPageSize] = useState(0);
  const [isAppliedFilter, setIsAppliedFilter] = useState(false);
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const debounceQ = useDebouncedValue(searchQ, 500);

  useEffect(() => {
    checkApplyFilter();
    setPageSize(0);
  }, [debounceQ, filters.payment, filters.sort]);
  const {
    isLoading,
    totalInvoiceFilterCount,
    invoiceList,
    totalInvoiceValue,
    totalPaid,
    totalPaidCount,
    totalUnPaid,
    totalUnPaidCount,
    totalInvoiceCount,
    handleInvoice,
  } = useAllInvoiceReport({
    business,
    searchText: debounceQ,
    type,
    startPage: 0,
    endPage: 20 + pageSize,
    filters,
  });

  const InvoiceData = useMemo(() => {
    let MappedInvList = invoiceList?.map((invoiceDt) => {
      return {
        id: invoiceDt?.id,
        date: dateFormatInNumberWithSpace(invoiceDt?.transacted_at),
        inv_num: `${
          invoiceDt?.invoice_prefix || InvoicePageContent.defaultPrefix[type]
        } ${invoiceDt?.invoice_number}`,
        amount: Number(invoiceDt?.amount).toFixed(2),
        received: invoiceDt?.advance_amount,
        balance: Number(
          Number(invoiceDt?.amount) - Number(invoiceDt?.advance_amount),
        ).toFixed(2),
        profit: invoiceDt?.profit || 0,
        status: invoiceDt?.status,
        invoice_number: invoiceDt?.invoice_number,
        invoice_prefix: invoiceDt?.invoice_prefix,
      };
    });
    return MappedInvList;
  }, [invoiceList, debounceQ, filters]);

  const handleReport = () => {
    let pathNametoChange = `/${
      type === "out" ? "PurchaseInvoice" : "InvoiceBook"
    }/Reports`;
    triggerLogEvent(
      analytics,
      type === "out" ? "pb_purchase_report" : "inv_sales_report",
    );
    navigate(pathNametoChange, {
      state: {
        pathname: location.pathname,
      },
    });
  };

  const handleCreate = () => {
    let pathNametoChange = `/${
      type === "out" ? "PurchaseInvoice" : "InvoiceBook"
    }/Create`;
    triggerLogEvent(
      analytics,
      type === "out" ? "pb_create_purchase" : "inv_create_invoice",
    );
    navigate(pathNametoChange, {
      state: {
        pathname: location.pathname,
        data: {
          invoiceCount: invoiceList?.length,
        },
      },
    });
  };

  const handleAscending = (headInfo) => {
    if (headInfo?.onClick) {
      setFilters((f) => ({
        ...f,
        sort:
          f.sort === INVOICESORT.isAscendingOrder
            ? "Recent"
            : INVOICESORT.isAscendingOrder,
      }));
    }
  };

  const checkApplyFilter = () => {
    const isApplied =
      filters.payment !== "All" ||
      (filters.sort !== "Recent" &&
        filters.sort !== INVOICESORT.isAscendingOrder);
    setIsAppliedFilter(isApplied);
  };

  const invoiceStatusList = [
    {
      amount: formatNum(totalPaid),
      value: totalPaidCount,
      title: InvoicePageContent.invoice_info1[type],
      id: InvoicePageContent.invoice_info1[type],
      cirlceColor: "var(--foundationGreenDark)",
      rsLabel: true,
    },
    {
      amount: formatNum(totalUnPaid),
      value: totalUnPaidCount,

      title: InvoicePageContent.invoice_info2[type],
      id: InvoicePageContent.invoice_info2[type],
      cirlceColor: "var(--foundationOrange)",
      rsLabel: true,
    },
  ];
  const resetAllFilterApplied = () => {
    setFilters(initialFilters);
    setSearchQ("");
  };

  return (
    <MainContainer fluid>
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: type == "in" ? "sales_invoice" : "purchase_bill",
            }}
          >
            <UBButton
              title={type == "in" ? I18n.sales_report : I18n.purchase_report}
              id="inv_list_report_btn"
              className={"buttonTeritary marginLeft15"}
              onClick={handleReport}
            />
            <UBButton
              title={InvoicePageContent.create_btn[type]}
              id="inv_list_create_btn"
              className={
                "foundationBlueNormalBg foundationWhite fontSize14 fontWeight400 textUppercase marginLeft12"
              }
              iconComponent={<PlusIcon />}
              onClick={handleCreate}
            />
          </TitleAndActionBox>
          <InnerWrapper>
            <Box display={"flex"} justifyContent={"space-between"}>
              <AmountBox
                isNonIdeal={!InvoiceData.length}
                data={
                  InvoicePageContent.invoice_info3.displayOn === type
                    ? [
                        ...invoiceStatusList,
                        {
                          amount: formatNum(totalInvoiceValue),
                          title: InvoicePageContent.invoice_info3[type],
                          id: InvoicePageContent.invoice_info3[type],
                          cirlceColor: "var(--foundationBlueDark)",
                          rsLabel: true,
                        },
                      ]
                    : invoiceStatusList
                }
              />
              <Box display={"flex"} alignItems={"center"} height={"36px"}>
                <UBSearchBar
                  searchPlaceholder={
                    InvoicePageContent.search_placeholder[type]
                  }
                  query={searchQ}
                  setQuery={setSearchQ}
                  source={"purchase"}
                />
                <div
                  className="filter_btn"
                  role="button"
                  onClick={() => setShowFilter((f) => !f)}
                >
                  <FilterIcon />
                  {isAppliedFilter && <div className="redDotStyle" />}
                </div>
              </Box>
            </Box>

            <div
              id="sale_invoice_list"
              className="invoice_list_table"
              style={{ height: "calc(100% - 54px)" }}
              /* style={{ height: "calc(100% - 154px)" }} */
            >
              <div className="invoiceListTableHeader">
                {InvListTableHeads.map((headInfo, ind) => (
                  <div
                    className={`invListTableHeaderCol ${headInfo?.className}`}
                  >
                    {(!("displayOn" in headInfo) ||
                      headInfo.displayOn == type) && (
                      <ParagraphText
                        className={"fontSize14 fontWeight400 foundationInk300"}
                        key={headInfo.name}
                        onClick={() => { if (InvoiceData.length) handleAscending(headInfo); }}
                      >
                        {type === "in"
                          ? headInfo.name
                          : headInfo?.outName || headInfo?.name}
                        {headInfo.isArrow ? (
                          <SortArrow
                            isAscending={
                              filters.sort === INVOICESORT.isAscendingOrder
                            }
                            disabled={!InvoiceData.length}
                          />
                        ) : (
                          <></>
                        )}
                      </ParagraphText>
                    )}
                  </div>
                ))}
              </div>

              <div
                className="table_items generic-scrollbar"
                style={{ height: "calc(100% - 57px)" }}
                id="scrollableDiv"
              >
                {!isLoading ? (
                  totalInvoiceCount && !InvoiceData.length ? (
                    <InvoiceBookNonIdeal
                      onBack={resetAllFilterApplied}
                      buttonTitle={
                        type === "in"
                          ? I18n.go_back_invoice_listing
                          : I18n.go_back_bill_listing
                      }
                      title={I18n.no_result}
                      subTitle={
                        type === "in"
                          ? I18n.non_ideal_invoice_book
                          : I18n.non_ideal_bill_book
                      }
                    />
                  ) : (
                    <AppPagination
                      data={{
                        handleNext: (e) => setPageSize((value) => value + 20),
                        pageLoad: true,
                        count: totalInvoiceFilterCount,
                        list: InvoiceData,
                        scrollableTarget: "scrollableDiv",
                      }}
                    >
                      {InvoiceData?.map((invDat, ind) => {
                        const getInvoicebyID = invoiceList.find(
                          (val) => val.id === invDat.id,
                        );
                        return (
                          <InvoiceRowItem
                            key={invDat.id}
                            invoice={getInvoicebyID}
                            invoiceDetail={invDat}
                            refetchInvoiceList={handleInvoice}
                            navigate={navigate}
                            location={location}
                            type={type}
                          />
                        );
                      })}
                    </AppPagination>
                  )
                ) : (
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                    height={"100%"}
                  >
                    <CircularProgress />
                  </Box>
                )}
              </div>
            </div>
            <FilterModal
              isOpen={showFilter}
              handleClose={setShowFilter.bind(this, false)}
              setFilters={setFilters}
              filter={filters}
              type={type}
            />
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};
const enhance = withObservables([], ({ location, type }) => ({
  invoiceList:
    type === "in"
      ? observeSaleInvoicesByBusiness(localStorage.getItem("business_id"))
      : observeBillInvoicesByBusiness(localStorage.getItem("business_id")),
}));
export default enhance(InvoiceList);
